/**
 * 10.5O — MISSÕES com recompensa. Três objetivos ativos ao mesmo tempo (abater, elites, baús,
 * explorar territórios, boss). Não tem timer/energia/daily (a mesma filosofia do Rift Surge):
 * 10.6F — a recompensa agora é COLETADA pelo jogador. Antes ela caía sozinha e a missão
 * era trocada no mesmo frame: o prêmio passava batido e nem dava para ver o que rendeu.
 * Agora a missão cumprida FICA na lista marcada como pronta, esperando o toque em Coletar.
 * Estado puro — quem paga a recompensa é o Game.
 */
import { GameConfig } from "../config/GameConfig";

export type MissionKind = "kill" | "elite" | "chest" | "boss" | "territory";

export interface MissionState {
  id: string; // id do template
  kind: MissionKind;
  progress: number;
  target: number;
  coins: number;
  xp: number;
  shards: number;
}

export interface MissionsSnapshot {
  active: { id: string; progress: number; target: number }[];
  done: Record<string, number>;
}

interface Template {
  id: string;
  kind: MissionKind;
  target: number;
  coins: number;
  xp: number;
  shards: number;
}

export class Missions {
  private active: MissionState[] = [];
  private done: Record<string, number> = {};
  private readonly visited = new Set<string>();

  constructor(private readonly playerLevel: () => number) {}

  get list(): readonly MissionState[] {
    return this.active;
  }
  /** Missão que aparece no HUD: uma PRONTA na frente de tudo, senão a primeira em aberto. */
  get focus(): MissionState | null {
    return this.active.find((m) => m.progress >= m.target) ?? this.active.find((m) => m.progress < m.target) ?? this.active[0] ?? null;
  }
  /** 10.6F — missões cumpridas esperando o jogador coletar. */
  get ready(): MissionState[] {
    return this.active.filter((m) => m.progress >= m.target);
  }
  get hasReady(): boolean {
    return this.active.some((m) => m.progress >= m.target);
  }

  /**
   * 10.6F — COLETA a recompensa de uma missão pronta: tira da lista, conta como cumprida e
   * repõe o slot. Devolve a missão (para o Game pagar) ou null se ela não estava pronta.
   */
  claim(id: string): MissionState | null {
    const i = this.active.findIndex((m) => m.id === id && m.progress >= m.target);
    if (i < 0) return null;
    const m = this.active[i];
    this.active.splice(i, 1);
    this.done[m.id] = this.timesDone(m.id) + 1;
    if (m.kind === "territory") this.visited.clear();
    this.refill();
    return m;
  }
  timesDone(id: string): number {
    return this.done[id] ?? 0;
  }

  /** Garante `slots` missões ativas (usado no load e após concluir). */
  refill(): void {
    const cfg = GameConfig.missions;
    let guard = 0;
    while (this.active.length < cfg.slots && guard++ < 20) {
      const next = this.pickTemplate();
      if (!next) break;
      this.active.push(this.instantiate(next));
    }
  }

  /** Sorteia um template que ainda não está ativo (o menos feito primeiro). */
  private pickTemplate(): Template | null {
    const pool = (GameConfig.missions.templates as readonly Template[]).filter((t) => !this.active.some((a) => a.id === t.id));
    if (pool.length === 0) return null;
    pool.sort((a, b) => this.timesDone(a.id) - this.timesDone(b.id));
    // entre os menos feitos, escolhe um aleatório (variedade sem repetir sempre o mesmo)
    const best = this.timesDone(pool[0].id);
    const tie = pool.filter((t) => this.timesDone(t.id) === best);
    return tie[Math.floor(Math.random() * tie.length)];
  }

  /** Alvo e prêmio crescem com o nível do player e com quantas vezes já foi cumprida. */
  private instantiate(t: Template): MissionState {
    const c = GameConfig.missions;
    const rep = this.timesDone(t.id);
    const lvl = Math.max(1, this.playerLevel());
    const growth = 1 + rep * c.repeatGrowth;
    return {
      id: t.id,
      kind: t.kind,
      progress: 0,
      target: Math.max(1, Math.round(t.target * growth)),
      coins: Math.round((t.coins + lvl * c.coinsPerLevel) * growth),
      xp: Math.round((t.xp + lvl * c.xpPerLevel) * growth),
      shards: t.shards > 0 ? Math.max(1, Math.round(t.shards * growth)) : 0,
    };
  }

  /** Registra progresso; devolve as que ACABARAM de ficar prontas (para avisar na tela).
   *  Não paga nada e não tira da lista — quem tira é `claim`. */
  advance(kind: MissionKind, amount = 1): MissionState[] {
    const finished: MissionState[] = [];
    for (const m of this.active) {
      if (m.kind !== kind || m.progress >= m.target) continue;
      m.progress = Math.min(m.target, m.progress + amount);
      if (m.progress >= m.target) finished.push(m);
    }
    return finished;
  }

  /** Explorar: só conta território NOVO (não fica indo e voltando). */
  visitTerritory(id: string): MissionState[] {
    if (!id || this.visited.has(id)) return [];
    this.visited.add(id);
    return this.advance("territory");
  }

  snapshot(): MissionsSnapshot {
    return {
      active: this.active.map((m) => ({ id: m.id, progress: m.progress, target: m.target })),
      done: { ...this.done },
    };
  }

  load(data: MissionsSnapshot | undefined): void {
    this.active = [];
    this.done = {};
    this.visited.clear();
    if (data?.done && typeof data.done === "object") {
      for (const [k, v] of Object.entries(data.done)) {
        if (typeof v === "number" && Number.isFinite(v)) this.done[k] = Math.max(0, Math.floor(v));
      }
    }
    for (const a of data?.active ?? []) {
      const t = (GameConfig.missions.templates as readonly Template[]).find((x) => x.id === a?.id);
      if (!t || this.active.some((m) => m.id === t.id)) continue;
      const m = this.instantiate(t);
      // preserva o progresso salvo (clampado ao alvo recalculado)
      m.target = Math.max(1, Math.floor(Number(a.target) || m.target));
      m.progress = Math.max(0, Math.min(m.target, Math.floor(Number(a.progress) || 0)));
      this.active.push(m);
    }
    this.refill();
  }

  reset(): void {
    this.active = [];
    this.done = {};
    this.visited.clear();
    this.refill();
  }
}
